<template>
  <div
    ref="imageCarousel"
    class="c-image-carousel"
  >
    <CIconButton
      class="c-image-carousel__button c-image-carousel__button--left"
      title="Visa föregående bild"
      name="chevron_left"
      variant="plain"
      @click="prev"
    />
    <div
      class="c-image-carousel__viewport"
      :style="viewportStyle"
    >
      <div
        class="c-image-carousel__thumbnails"
        :style="thumbnailsStyle"
      >
        <CImage
          v-for="(image, index) in images"
          :key="'carousel-image-' + index"
          :class="{
            'c-image-carousel__thumbnail--active': imageIndex === index,
          }"
          :src="[image]"
          :alt="altText"
          :height="thumbnailSize"
          :width="thumbnailSize"
          :style="thumbnailStyle"
          @click="imageClicked(image, index)"
        />
      </div>
    </div>
    <CIconButton
      class="c-image-carousel__button c-image-carousel__button--right"
      title="Visa nästa bild"
      name="chevron_right"
      variant="plain"
      @click="next"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, ref, onMounted, onBeforeUnmount, watch } from 'vue'
import CImage from '../../atoms/CImage'
import CIconButton from '../../molecules/CIconButton'
import type { Image } from './types'

type Props = {
  images: Image[]
  altText?: string
  carouselImageCount?: number
  thumbnailSize?: number
  thumbnailMargin?: number
  useKeyboardNavigation?: boolean
  startIndex?: number
}

const props = withDefaults(defineProps<Props>(), {
  altText: '',
  carouselImageCount: 4,
  thumbnailSize: 80,
  thumbnailMargin: 24,
  useKeyboardNavigation: false,
  startIndex: undefined,
})

const emit = defineEmits<{
  (e: 'click', image: Image): void
}>()

const imageCarousel = ref<HTMLElement | null>(null)
const imageIndex = ref(0)
const start = ref<number | null>(null)
const end = ref<number | null>(null)

const viewportStyle = computed(() => {
  const imageMargin = props.thumbnailMargin
  const imageWidth = props.thumbnailSize
  const images = Math.min(props.images.length, props.carouselImageCount)
  const width = imageWidth * images + imageMargin * (images - 1) + 4
  return {
    width: `${width}px`,
  }
})

const thumbnailStyle = computed(() => {
  return `margin-right: ${props.thumbnailMargin}px`
})

const thumbnailsStyle = computed(() => {
  // When we have equal or less images than the carouselImageCount we don't want to translate
  if (props.images.length == props.carouselImageCount) {
    return undefined
  }

  const imageMargin = props.thumbnailMargin
  const imageWidth = props.thumbnailSize + imageMargin
  const translate =
    Math.min(imageIndex.value, props.images.length - props.carouselImageCount) *
    imageWidth

  return {
    transform: `translateX(-${translate}px)`,
  }
})

const onTouchStart = (event: TouchEvent) => {
  start.value = event.touches[0].clientX
  end.value = 0
}

const onTouchMove = (event: TouchEvent) => {
  end.value = event.touches[0].clientX
}

const onTouchEnd = () => {
  if (!end.value || Math.abs(end.value - (start.value as number)) < 20) {
    return
  }

  if ((start.value as number) < end.value) ctx.handleScroll(-1)
  else {
    ctx.handleScroll(1)
  }
}

const handleScroll = (value: number) => {
  const lastImageIndex = props.images.length - 1

  let newIndex = imageIndex.value + value

  if (newIndex < 0) {
    newIndex = props.images.length - 1
  } else if (newIndex > lastImageIndex) {
    newIndex = 0
  }

  imageIndex.value = newIndex
  emit('click', props.images[newIndex])
}

const imageClicked = (image: Image, index: number) => {
  emit('click', image)
  imageIndex.value = index
}

const next = () => {
  imageIndex.value = imageIndex.value + 1

  if (imageIndex.value > props.images.length - 1) {
    imageIndex.value = 0
  }

  emit('click', props.images[imageIndex.value])
}

const prev = () => {
  imageIndex.value = imageIndex.value - 1

  if (imageIndex.value < 0) {
    imageIndex.value = props.images.length - 1
  }

  emit('click', props.images[imageIndex.value])
}

const onKeyEvent = (e: KeyboardEvent) => {
  if (e.key === 'ArrowRight') {
    next()
  }
  if (e.key === 'ArrowLeft') {
    prev()
  }
}

const ctx = {
  handleScroll,
}

onMounted(() => {
  imageCarousel.value?.addEventListener('touchstart', onTouchStart)
  imageCarousel.value?.addEventListener('touchmove', onTouchMove)
  imageCarousel.value?.addEventListener('touchend', onTouchEnd)

  if (props.useKeyboardNavigation) {
    window.addEventListener('keydown', onKeyEvent)
  }
})

onBeforeUnmount(() => {
  imageCarousel.value?.removeEventListener('touchstart', onTouchStart)
  imageCarousel.value?.removeEventListener('touchmove', onTouchMove)
  imageCarousel.value?.removeEventListener('touchend', onTouchEnd)

  if (props.useKeyboardNavigation) {
    window.removeEventListener('keydown', onKeyEvent)
  }
})

watch(
  () => props.startIndex,
  (value) => {
    if (value !== undefined) {
      imageIndex.value = value
    }
  },
)

defineExpose({
  imageIndex,
  handleScroll,
  onTouchStart,
  onTouchEnd,
  start,
  end,
  ctx,
})
</script>

<script lang="ts">
import { defineComponent } from 'vue'

export default defineComponent({
  name: 'CImageCarousel',
})
</script>
